import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiGet, apiPut } from '../../generic/Api_Functions'
import { getVisionSummary } from '../visionApi'
import { V3_RANGES, VISION_ENABLED, displayValue, parseNumber, storedValue } from './cameraControlRules'

const VISION_SECTION = 'VISION'
const SUMMARY_REFRESH_MS = 15000

/**
 * Story 213, adenda C11.4 — the data of `Settings › Camera control`: the VISION rows of GET /settings, with the changes
 * not saved yet, and the summary of C11.2. Only asks Sun when `enabled` (the user has SETTINGS).
 */
const useCameraControl = (enabled, language) => {
  const [rows, setRows] = useState([])
  const [edited, setEdited] = useState({})
  const [summary, setSummary] = useState({})

  useEffect(() => {
    if (!enabled) {
      return
    }
    let cancelled = false
    apiGet('settings')
      .then((sections) => {
        const vision = (Array.isArray(sections) ? sections : []).find((section) => section.name === VISION_SECTION)
        if (!cancelled) {
          setRows(vision ? vision.configurations : [])
        }
      })
      .catch(() => {})
    // C11.2: the summary when the screen opens and every 15 s while it stays open.
    const refreshSummary = () => getVisionSummary()
      .then((answer) => {
        if (!cancelled) {
          setSummary(answer || {})
        }
      })
      .catch(() => {})
    refreshSummary()
    const timer = setInterval(refreshSummary, SUMMARY_REFRESH_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [enabled])

  const stored = useMemo(() => Object.fromEntries(rows.map((row) => [row.key, String(row.value)])), [rows])

  // The pending value, or the stored one; numbers in the format of the language (H-10).
  const values = useMemo(
    () => Object.fromEntries(rows.map((row) => [
      row.key,
      row.key in edited ? edited[row.key] : row.key in V3_RANGES ? displayValue(row.value, language) : String(row.value)
    ])),
    [rows, edited, language]
  )

  const setValue = useCallback((key, value) => {
    setEdited((previous) => ({ ...previous, [key]: value }))
  }, [])

  // RN101 (S270): a value is pending only while it differs from the stored one: numbers compared as numbers (a typed
  // "0,50" is the stored "0.5"), lists without case, as Sun reads them.
  const changedKeys = useMemo(
    () => Object.keys(edited).filter((key) => key in stored && key !== VISION_ENABLED && (key in V3_RANGES
      ? parseNumber(edited[key]) !== parseNumber(stored[key]) || Number.isNaN(parseNumber(edited[key]))
      : String(edited[key]).toUpperCase() !== String(stored[key]).toUpperCase())),
    [edited, stored]
  )

  const discard = useCallback(() => setEdited({}), [])

  // RN101, R-4 (S268, S269) and RN151 (S384, S385): a switch that saves at once, alone: a PUT with only its row. On success it
  // is the stored value; on a failure the caller gets the rejection and nothing changes here.
  const saveAtOnce = useCallback(async (key, value) => {
    const row = rows.find((candidate) => candidate.key === key)
    if (!row) {
      return
    }
    await apiPut('settings', [{ ...row, value }])
    setRows((previous) => previous.map((candidate) => (candidate.key === key ? { ...candidate, value } : candidate)))
  }, [rows])

  const saveEnabled = useCallback((value) => saveAtOnce(VISION_ENABLED, value), [saveAtOnce])

  // RN54: PUT /settings carries only the VISION_ rows: untouched as Sun gave them, the changed numbers with a point.
  const saveSettings = useCallback(
    () => apiPut('settings', rows.map((row) => {
      if (!(row.key in edited)) {
        return row
      }
      return { ...row, value: row.key in V3_RANGES ? storedValue(edited[row.key]) : edited[row.key] }
    })),
    [rows, edited]
  )

  return { rows, stored, values, setValue, summary, saveSettings, saveEnabled, saveAtOnce, changedKeys, discard }
}

export default useCameraControl
